/**
 * Pretty-print captured markup for display (REV-544).
 *
 * Findings store `element_html` exactly as scraped, which is very often a
 * single minified line — an unbroken wall of markup that forces horizontal
 * scrolling and tells a remediator nothing at a glance.
 *
 * Two constraints shape this:
 *
 * 1. The input is a FRAGMENT lifted out of a live page, so it is regularly
 *    unbalanced or invalid. Prettier throws on input it cannot parse. A finding
 *    must never fail to render because its own markup would not parse, so every
 *    failure path returns the input untouched.
 * 2. Prettier's browser build plus the HTML parser is ~260KB. It is therefore
 *    imported dynamically, so it is fetched only when a code block is actually
 *    displayed rather than on every page of the app.
 *
 * The version is pinned EXACTLY in package.json: Prettier changes its output in
 * minor releases, so a caret range would silently reformat every code block on
 * an unrelated dependency bump.
 */

/** Cached across calls so the 260KB chunk is fetched at most once per session. */
let prettierPromise: Promise<{
  format: (src: string, opts: Record<string, unknown>) => Promise<string>;
  htmlPlugin: unknown;
}> | null = null;

async function loadPrettier() {
  if (!prettierPromise) {
    prettierPromise = Promise.all([import('prettier/standalone'), import('prettier/plugins/html')])
      .then(([standalone, html]) => ({
        format: standalone.format as (
          src: string,
          opts: Record<string, unknown>
        ) => Promise<string>,
        htmlPlugin: (html as { default?: unknown }).default ?? html,
      }))
      .catch((err) => {
        // A failed chunk fetch must not poison every later call.
        prettierPromise = null;
        throw err;
      });
  }
  return prettierPromise;
}

/**
 * Format an HTML fragment for display.
 *
 * @param src raw captured markup
 * @returns formatted markup, or `src` unchanged if it cannot be parsed
 */
export async function formatHtmlFragment(src: string): Promise<string> {
  if (!src || !src.trim()) {
    return src;
  }
  try {
    const { format, htmlPlugin } = await loadPrettier();
    return await format(src, {
      parser: 'html',
      plugins: [htmlPlugin],
      printWidth: 80,
      htmlWhitespaceSensitivity: 'ignore',
    });
  } catch {
    // Unparseable fragment, or the chunk failed to load. Showing the raw
    // capture is always better than showing nothing.
    return src;
  }
}
